/**
 * Mobile bottom nav — Cart tab opens theme cart drawer + count badge.
 */
import { StandardEvents } from '@shopify/events';

const MOBILE_MQ = window.matchMedia('(max-width: 749px)');
const CART_DRAWER_ID = 'cart-drawer';
const ACTIVE_CLASS = 'mobile-bottom-nav__item--cart-open';

/** @returns {import('@theme/theme-drawer').ThemeDrawer | null} */
function getCartDrawer() {
  const drawer = document.getElementById(CART_DRAWER_ID);
  return drawer && typeof drawer.open === 'function' ? drawer : null;
}

/** @param {number} count */
function updateCartCount(count) {
  const safeCount = Math.max(0, Number(count) || 0);

  document.querySelectorAll('[data-mobile-cart-count]').forEach((el) => {
    if (!(el instanceof HTMLElement)) return;
    el.textContent = safeCount > 99 ? '99+' : String(safeCount);
    el.classList.toggle('visually-hidden', safeCount === 0);
  });
}

/** @param {boolean} isOpen */
function setCartTabOpen(isOpen) {
  document.querySelectorAll('[data-mobile-cart-trigger]').forEach((btn) => {
    if (!(btn instanceof HTMLElement)) return;
    btn.classList.toggle(ACTIVE_CLASS, isOpen);
  });
}

function recoverCartDrawerIfDesynced() {
  const drawer = getCartDrawer();
  if (!drawer || typeof drawer.recoverFromExternalDialogClose !== 'function') return;

  const panel = drawer.querySelector('dialog');
  if (drawer.hasAttribute('open') && panel && !panel.open) {
    drawer.recoverFromExternalDialogClose();
  }
}

function openCartDrawer() {
  if (!MOBILE_MQ.matches) return;
  recoverCartDrawerIfDesynced();
  getCartDrawer()?.open();
}

/** @param {import('@shopify/events').CartLinesUpdateEvent} event */
function handleCartUpdate(event) {
  event.promise
    ?.then((result) => {
      const count = result?.cart?.totalQuantity ?? result?.detail?.cartItemCount;
      if (typeof count === 'number') updateCartCount(count);
    })
    .catch(() => {});
}

function syncCartCountFromStorage() {
  try {
    const raw = sessionStorage.getItem('cart-count');
    if (!raw) return;
    const { value } = JSON.parse(raw);
    const count = parseInt(value, 10);
    if (Number.isFinite(count)) updateCartCount(count);
  } catch {
    /* no-op */
  }
}

function initMobileCartNav() {
  document.querySelectorAll('[data-mobile-cart-trigger]').forEach((trigger) => {
    trigger.addEventListener('click', (event) => {
      event.preventDefault();
      openCartDrawer();
    });
  });

  document.addEventListener('theme-drawer:open', (event) => {
    if (/** @type {Element} */ (event.target).id === CART_DRAWER_ID) {
      setCartTabOpen(true);
    }
  });

  document.addEventListener('theme-drawer:close', (event) => {
    if (/** @type {Element} */ (event.target).id === CART_DRAWER_ID) {
      setCartTabOpen(false);
    }
  });

  document.addEventListener(StandardEvents.cartLinesUpdate, handleCartUpdate);
  syncCartCountFromStorage();
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') return;
    recoverCartDrawerIfDesynced();
    setCartTabOpen(Boolean(getCartDrawer()?.hasAttribute('open')));
    syncCartCountFromStorage();
  });
}

initMobileCartNav();
